// Ca 2026-08-18 (ducna1@nal.vn) — vá CA store của Python cho hook telemetry.
//
// Triệu chứng gốc: 104 lần `SSLCertVerificationError` liên tiếp, `last_ack_ts` null
// (chưa gửi nổi MỘT event nào kể từ khi cài), trong khi `avatar doctor` xanh 17 mục.
// Nguyên nhân: `/Library/Frameworks/Python.framework/Versions/3.13/etc/openssl/` RỖNG
// (`ls` ra `total 0`) ⇒ `ssl.get_default_verify_paths().cafile` = None ⇒ mọi `urlopen`
// chết ở bắt tay TLS. Bundle `certifi` VẪN có sẵn trong site-packages — chỉ thiếu đúng
// một symlink trỏ vào nó.
//
// ⚠️ `Install Certificates.command` KHÔNG cứu được ca đó: chạy xong in "update
// complete" mà symlink vẫn không có (nó không fail loud khi thiếu quyền ghi). Chỉ dẫn
// cũ của probe khuyên đúng lệnh này ⇒ người dùng làm theo, tưởng đã sửa, và im lặng
// thêm nửa ngày. Vì vậy ở đây ĐO trước rồi mới vá, không khuyên suông.

import { promises as fs } from "node:fs";
import { dirname } from "node:path";

/** Ảnh chụp CA store, đo bằng CHÍNH `python3` mà hook telemetry dùng. */
export interface PythonCaStoreState {
  /** `ssl.get_default_verify_paths().cafile` — null khi file KHÔNG tồn tại. */
  cafile: string | null;
  /** `openssl_cafile` — đường Python SẼ đọc nếu file có mặt; có giá trị kể cả khi trống. */
  opensslCafile: string | null;
  /** `certifi.where()` — bundle CA đi kèm pip. */
  certifiPath: string | null;
  certifiExists: boolean;
  /** Đích symlink CÓ SẴN file chưa. */
  opensslCafileExists: boolean;
  /** Giá trị env `SSL_CERT_FILE` — nó ghi đè mọi thứ, kể cả store lành. */
  sslCertFileEnv: string | null;
}

export type CaStoreRepairPlan =
  /** Store trống + có certifi ⇒ vá được bằng đúng một symlink. */
  | { kind: "symlink"; from: string; to: string }
  /** Store trống nhưng KHÔNG có certifi ⇒ phải cài bundle trước, CLI không tự cài. */
  | { kind: "install_certifi" }
  /** Store CÓ mà vẫn verify fail ⇒ KHÔNG phải ca này (nghi TLS interception). */
  | { kind: "store_present"; cafile: string }
  /** Store lành nhưng `SSL_CERT_FILE` trỏ đi chỗ khác ⇒ sửa env, TUYỆT ĐỐI không symlink. */
  | { kind: "env_override_broken"; env: string | null; cafileTarget: string }
  /** Không đủ dữ kiện (probe cũ/không đo được) ⇒ không được đoán mò rồi ghi đè. */
  | { kind: "unknown_state"; reason: string };

/**
 * Quyết định vá gì. Thuần — không chạm filesystem nên test được đầy đủ.
 *
 * BẤT BIẾN: `cafile` non-null ⇒ TUYỆT ĐỐI không symlink. Store đã có mà TLS vẫn fail
 * nghĩa là chứng chỉ đến từ CA lạ (proxy chặn/TLS interception); đè symlink lên đó
 * vừa vô ích vừa xoá mất cấu hình CA nội bộ mà IT có thể đã cài.
 */
export function planCaStoreRepair(state: PythonCaStoreState): CaStoreRepairPlan {
  if (state.cafile) return { kind: "store_present", cafile: state.cafile };
  if (!state.opensslCafile) {
    return { kind: "unknown_state", reason: "không đọc được openssl_cafile của python3" };
  }
  // Đích đã có file mà Python vẫn không đọc được ⇒ env đang lái đi chỗ khác. Vá bằng
  // symlink ở đây là XOÁ một cert.pem đang lành để chữa một triệu chứng do env gây ra.
  if (state.opensslCafileExists) {
    return {
      kind: "env_override_broken",
      env: state.sslCertFileEnv,
      cafileTarget: state.opensslCafile,
    };
  }
  if (!state.certifiExists || !state.certifiPath) return { kind: "install_certifi" };
  return { kind: "symlink", from: state.certifiPath, to: state.opensslCafile };
}

export interface CaStoreRepairResult {
  ok: boolean;
  message: string;
  /** Lệnh người dùng chạy tay khi CLI không đủ quyền ghi. KHÔNG tự chạy sudo. */
  sudoCommand?: string;
}

/**
 * Thi hành plan. Chỉ `symlink` mới ghi filesystem; mọi plan khác trả về nguyên trạng.
 * Idempotent: symlink đã trỏ đúng đích thì không đụng gì.
 *
 * KHÔNG tự nâng quyền: `/Library/Frameworks/...` là thư mục hệ thống, chạy `sudo` hộ
 * người dùng trong một lệnh chẩn đoán là vượt quyền. Thiếu quyền ⇒ trả lệnh để họ tự
 * quyết định.
 */
export async function applyCaStoreRepair(plan: CaStoreRepairPlan): Promise<CaStoreRepairResult> {
  if (plan.kind !== "symlink") {
    return { ok: false, message: describeCaStoreRepairPlan(plan) };
  }

  const sudoCommand = `sudo mkdir -p ${dirname(plan.to)} && sudo ln -sf ${plan.from} ${plan.to}`;
  try {
    if (await symlinkAlreadyCorrect(plan.to, plan.from)) {
      return { ok: true, message: `CA store đã trỏ đúng ${plan.from} — không cần sửa.` };
    }
    // TOCTOU: plan dựng từ ảnh chụp lúc probe chạy; giữa lúc đó và lúc --fix, đích có
    // thể đã thành FILE THẬT (người dùng vừa chạy Install Certificates.command, IT vừa
    // đặt CA nội bộ). Xoá nó đi là phá dữ liệu không dựng lại được ⇒ chỉ thay thứ mình
    // biết chắc là symlink. Phát hiện khi tự review PR 2026-08-18.
    const existing = await lstatOrNull(plan.to);
    if (existing && !existing.isSymbolicLink()) {
      return {
        ok: false,
        message: `${plan.to} là FILE THẬT chứ không phải symlink — không xoá. Kiểm tay rồi quyết định.`,
      };
    }
    await fs.mkdir(dirname(plan.to), { recursive: true });
    // rm trước rồi symlink: `fs.symlink` ném EEXIST khi đích đã tồn tại, kể cả khi đó
    // là symlink GÃY — mà symlink gãy chính là ca hay gặp sau khi gỡ/nâng cấp Python.
    await fs.rm(plan.to, { force: true });
    await fs.symlink(plan.from, plan.to);
    return { ok: true, message: `Đã trỏ CA store ${plan.to} -> ${plan.from}` };
  } catch (err) {
    const code = (err as NodeJS.ErrnoException)?.code;
    if (code === "EACCES" || code === "EPERM") {
      return { ok: false, message: `Không đủ quyền ghi ${plan.to}.`, sudoCommand };
    }
    return {
      ok: false,
      message: `Vá CA store thất bại (${code ?? (err instanceof Error ? err.message : String(err))}).`,
      sudoCommand,
    };
  }
}

async function lstatOrNull(p: string) {
  try {
    return await fs.lstat(p);
  } catch {
    return null;
  }
}

async function symlinkAlreadyCorrect(link: string, target: string): Promise<boolean> {
  try {
    const stat = await fs.lstat(link);
    if (!stat.isSymbolicLink()) return false;
    return (await fs.readlink(link)) === target;
  } catch {
    return false;
  }
}

/** Câu giải thích cho từng plan — dùng cả ở detail của check lẫn thông báo lỗi khi fix. */
export function describeCaStoreRepairPlan(plan: CaStoreRepairPlan): string {
  switch (plan.kind) {
    case "symlink":
      return `CA store của Python TRỐNG (${plan.to} không tồn tại) nên mọi kết nối HTTPS của hook đều chết. Chạy \`avatar doctor --fix\` để trỏ vào bundle certifi (${plan.from}).`;
    case "install_certifi":
      return "CA store của Python TRỐNG và máy chưa có bundle `certifi`. Chạy `python3 -m pip install --upgrade certifi` rồi `avatar doctor --fix`.";
    case "env_override_broken":
      return `Đích CA (${plan.cafileTarget}) VẪN có file, nhưng Python không dùng nó vì biến môi trường SSL_CERT_FILE đang trỏ đi chỗ khác (${plan.env ?? "?"}). Sửa/bỏ biến đó rồi thử lại — KHÔNG vá bằng symlink, sẽ xoá mất cấu hình CA đang lành.`;
    case "store_present":
      return `Python CÓ đọc được CA store (${plan.cafile}) mà vẫn verify fail ⇒ nghi proxy/TLS interception, KHÔNG phải thiếu chứng chỉ gốc. Kiểm: \`echo | openssl s_client -connect telemetry.nal.vn:443 -servername telemetry.nal.vn | grep -E "^ *[0-9] s:|i:"\` — issuer không phải CA công cộng thì báo IT hạ tầng.`;
    default:
      // `unknown_state` — probe cũ hoặc python không đo được. Không đoán, hỏi dữ kiện.
      return `Không đo được CA store (${plan.reason}). Chạy tay: \`python3 -c "import ssl;print(ssl.get_default_verify_paths())"\` và gửi kết quả cho team Avatar.`;
  }
}
